import { merge } from "lodash";
import { DateTimeUnit } from "luxon";
import { Zoom } from "./Zoom";
import { ZoomSpanLimits } from "./ZoomSpanLimits";
import { TimeBandAxisZoomOptions } from "./TimeBandAxisZoomOptions";
import { ZoomableTimeBandAxis } from "./ZoomableTimeBandAxis";

const defaultTimeBandAxisZoomOptions: TimeBandAxisZoomOptions = {
  preferredTicks: 12,
  minTickSize: 100,
  maxTickSize: 200,
};

export const resolveTimeBandAxisZoom = (
  xAxis: ZoomableTimeBandAxis,
  timeUnit: DateTimeUnit,
  xAxisWidth: number | undefined,
  zoomAlignment: "start" | "end" = "end"
): Zoom | undefined => {
  if (
    xAxisWidth === undefined ||
    !(xAxis.zoom === true || xAxis.zoom !== undefined)
  )
    return undefined;

  const options: TimeBandAxisZoomOptions = merge(
    {},
    defaultTimeBandAxisZoomOptions,
    typeof xAxis.zoom === "object" ? xAxis.zoom : undefined
  );

  const xAxisStartDate = xAxis.min;
  const xAxisEndDate = xAxis.max;

  // Calculate total ticks in the range (ceil to include partial)
  const totalTicks = Math.max(
    1,
    Math.ceil(xAxisEndDate.diff(xAxisStartDate, timeUnit).get(timeUnit) ?? 0)
  );

  // Determine how many ticks can fit while keeping px per unit >= minUnitTickSize
  const minTickPx = Math.max(1, options.minTickSize);
  const ticksThatFitAtMin = Math.max(1, Math.floor(xAxisWidth / minTickPx));

  // Fit as many ticks as possible without dropping below minUnitTickSize
  const ticksToShow = Math.min(totalTicks, ticksThatFitAtMin);

  // end must be 100 to show the "last" part of the series
  let start: number;
  let end: number;

  if (ticksToShow >= totalTicks) {
    // Everything fits: show full range
    start = 0;
    end = 100;
  } else if (zoomAlignment === "start") {
    // Align zoom window to the beginning of the range
    start = 0;
    end = Math.max(0, Math.min(100, (ticksToShow / totalTicks) * 100));
  } else {
    // Align zoom window to the end of the range
    start = Math.max(0, Math.min(100, 100 - (ticksToShow / totalTicks) * 100));
    end = 100;
  }

  return {
    start,
    end,
  };
};

/**
 * Compute minSpan and maxSpan ensuring:
 * - maxSpan respects minUnitTickSize (px per unit >= minUnitTickSize)
 * - minSpan respects maxUnitTickSize (px per unit <= maxUnitTickSize)
 */
export const resolveTimeBandAxisZoomSpanLimits = (
  xAxis: ZoomableTimeBandAxis,
  timeUnit: DateTimeUnit,
  width: number | undefined
): ZoomSpanLimits => {
  // Fallback when width is not yet known
  if (width === undefined || width <= 0) {
    return { minSpan: 20, maxSpan: 80 };
  }

  const options: TimeBandAxisZoomOptions = merge(
    {},
    defaultTimeBandAxisZoomOptions,
    typeof xAxis.zoom === "object" ? xAxis.zoom : undefined
  );

  const xAxisStartDate = xAxis.min;
  const xAxisEndDate = xAxis.max;

  // Base unit: months (band axis groups by months)
  const totalMonthsFloat =
    xAxisEndDate.diff(xAxisStartDate, timeUnit).get(timeUnit) ?? 0;
  const totalMonths = Math.max(1, Math.ceil(totalMonthsFloat));

  const minTickPx = Math.max(1, options.minTickSize);
  const maxTickPx = Math.max(1, options.maxTickSize);

  // Compute unit counts to satisfy pixel constraints
  const monthsForMinTick = Math.max(1, Math.floor(width / minTickPx)); // px/unit >= minTickPx
  const monthsForMaxTick = Math.max(1, Math.ceil(width / maxTickPx)); // px/unit <= maxTickPx

  // Convert to spans in percentage
  let maxSpan = Math.min(
    100,
    (Math.min(totalMonths, monthsForMinTick) / totalMonths) * 100
  );
  let minSpan = Math.min(
    100,
    (Math.min(totalMonths, monthsForMaxTick) / totalMonths) * 100
  );

  // Ensure ordering: minSpan should not exceed maxSpan
  if (minSpan > maxSpan) {
    minSpan = maxSpan;
  }

  // Guard against NaN or invalid values
  if (!isFinite(minSpan)) minSpan = 20;
  if (!isFinite(maxSpan)) maxSpan = 80;

  return { minSpan, maxSpan };
};
